import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, toFullUrl } from '../../config/api';
import { Gift, Trophy, Users, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function RaffleSection({ siteSettings, isVisible }) {
  const [raffles, setRaffles] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/raffles?active_only=true`).then(({ data }) => {
      if (alive) setRaffles(data || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!isVisible('raffles')) return null;
  if (raffles.length === 0) return null;

  return (
    <section className="py-16 px-4" data-testid="raffles-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-cabinet text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-2 flex items-center gap-3">
          <Gift className="w-10 h-10 text-[#DAA520]" />
          {siteSettings?.section_titles?.raffles || 'GIVEAWAYS'}
        </h2>
        <p className="text-sm text-[#A3A3A3] mb-8">Enter for free. We'll email the winner.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {raffles.slice(0, 6).map((r) => (
            <button
              key={r.id}
              onClick={() => setSelected(r)}
              className="text-left bg-[#141414] border border-[#DAA520]/20 hover:border-[#DAA520] transition-colors overflow-hidden group"
              data-testid={`raffle-card-${r.id}`}
            >
              {r.cover_image && <img src={toFullUrl(r.cover_image)} alt={r.title} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" />}
              <div className="p-4 space-y-2">
                <h3 className="font-cabinet font-black uppercase text-lg truncate">{r.title}</h3>
                <p className="text-sm text-[#A3A3A3] truncate">Prize: <span className="text-[#DAA520] font-bold">{r.prize}</span></p>
                <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-[#737373]">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {r.entry_count} entries</span>
                  {r.end_date && <span>Ends {r.end_date}</span>}
                </div>
                <div className="pt-2 text-[#DAA520] font-bold text-xs uppercase">ENTER →</div>
              </div>
            </button>
          ))}
        </div>
      </div>
      {selected && <RaffleEntryDialog raffle={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}

function RaffleEntryDialog({ raffle, onClose }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const tiers = raffle.paid_tiers || [];

  const handleBuyEntries = async (tierIndex) => {
    if (!email.trim()) {
      toast.error('Email required to buy entries');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/raffles/${raffle.id}/buy-entries`, {
        email: email.trim(), name: name.trim(), tier_index: tierIndex,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Checkout failed');
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/raffles/${raffle.id}/enter`, { email, name });
      setTicket(data.ticket);
      toast.success('You\'re in!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Entry failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141414] border-2 border-[#DAA520]/40 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="raffle-entry-dialog">
        <div className="border-b border-[#262626] p-4 flex items-center justify-between">
          <h3 className="font-cabinet font-black uppercase">{raffle.title}</h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {ticket ? (
          <div className="p-8 text-center space-y-4" data-testid="raffle-entry-success">
            <Trophy className="w-12 h-12 text-[#22C55E] mx-auto" />
            <p className="text-lg font-bold">YOU'RE ENTERED!</p>
            <div className="bg-[#0A0A0A] border border-[#262626] p-3">
              <p className="text-[10px] uppercase text-[#A3A3A3]">Your Ticket</p>
              <p className="text-xl font-mono font-bold text-[#DAA520]">{ticket}</p>
            </div>
            <p className="text-xs text-[#737373]">Check your email for confirmation.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-3">
            <p className="text-sm text-[#A3A3A3]">Prize: <span className="text-[#DAA520] font-bold">{raffle.prize}</span></p>
            {raffle.description && <p className="text-xs text-[#737373]">{raffle.description}</p>}
            <input type="email" required placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm" data-testid="raffle-entry-email" />
            <input placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm" data-testid="raffle-entry-name" />
            <button type="submit" disabled={submitting}
              className="w-full h-12 bg-[#DAA520] hover:bg-white hover:text-[#0A0A0A] text-black font-black uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              data-testid="raffle-entry-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> ENTERING…</> : 'ENTER FOR FREE'}
            </button>
            {tiers.length > 0 && (
              <div className="pt-4 border-t border-[#262626] space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] flex items-center gap-1">
                  <Trophy className="w-3 h-3" /> Boost your odds — buy bonus tickets
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {tiers.map((tier, i) => (
                    <button
                      key={i}
                      type="button"
                      disabled={submitting || !email.trim()}
                      onClick={() => handleBuyEntries(i)}
                      className="border border-[#262626] hover:border-[#22C55E] hover:text-[#22C55E] p-2 text-center transition-colors disabled:opacity-40 disabled:hover:border-[#262626] disabled:hover:text-white"
                      data-testid={`raffle-buy-tier-${i}`}
                    >
                      <p className="font-mono font-bold text-sm">${tier.price.toFixed(2)}</p>
                      <p className="text-[10px] uppercase text-[#A3A3A3] mt-0.5">{tier.label || `${tier.entries} entries`}</p>
                    </button>
                  ))}
                </div>
                {!email.trim() && (
                  <p className="text-[10px] text-[#525252] text-center">Enter email above to enable</p>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
